import * as React from "react";
import { useResource, Resources } from "Translations/Resources";
import { AuthenticatedLayout } from "Components/Layout/AuthenticatedLayout";
import { useRouteMatch, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  getInvoiceItemCategoryDetailAsync,
  saveInvoiceItemCategoryAsync,
  getDivisionsAsync,
} from "State/InvoiceItemCategories/InvoiceItemCategoriesReducer";
import { RootStateType } from "State/Store";
import { PrimarySkeleton } from "Components/Shared/PrimarySkeleton";
import styled from "styled-components";
import { PrimaryButton } from "Components/Shared/Buttons/PrimaryButton";
import { BreadcrumbItem } from "Components/Layout/Breadcrumbs";
import { TextInput } from "Components/Shared/Inputs/TextInput";
import { Select, MenuItem } from "@material-ui/core";

type RouteProps = {
  code: string;
};

const StyledFormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing(1, 3)};
  margin-bottom: ${props => props.theme.spacing(3)}px;
`;

const StyledBasicInfo = styled.div`
  margin-bottom: ${props => props.theme.spacing(4)}px;

  .ibis-MuiInputBase-input.Mui-disabled {
    background-color: ${props => props.theme.colors.border};
    border-radius: ${props => props.theme.borderRadius};
    cursor: not-allowed;
  }

  .selectbox {
    .ibis-MuiInputBase-root {
      display: flex;

      &:before,
      &:after {
        display: none;
      }

      .ibis-MuiSelect-nativeInput {
        top: 0;
      }

      .ibis-MuiSelect-select.ibis-MuiSelect-select {
        border: 1px solid ${props => props.theme.colors.border};
        border-radius: ${props => props.theme.borderRadius};
        padding: 9px 12px;
      }

      &.Mui-focused,
      &:focus {
        background-color: transparent;
      }

      .ibis-MuiSelect-select {
        &:focus {
          background-color: transparent;
        }
      }

      .ibis-MuiSvgIcon-root {
        right: 5px;
      }
    }
  }
`;

const StyledTitle = styled.label`
  color: ${props => props.theme.palette.secondary.main};
  display: inline-block;
`;

type ValidationErrors = {
  name?: string;
  isCode?: string;
};

const BLOGIC_DIVISION_NAME = "Blogic";

export const InvoiceItemCategoryDetailPage: React.FunctionComponent<{
  isCreate: boolean;
}> = ({ isCreate }) => {
  const { t } = useResource();
  const history = useHistory();
  const match = useRouteMatch<RouteProps>();
  const code = match?.params?.code;
  const dispatch = useDispatch();
  const { detail, isLoading, isUpdating, saveSuccess, divisions } = useSelector(
    (e: RootStateType) => e.invoiceItemCategories,
  );

  const [name, setName] = React.useState("");
  const [isCode, setIsCode] = React.useState("");
  const [divisionID, setDivisionID] = React.useState<any>("");
  const [errors, setErrors] = React.useState<ValidationErrors>({});

  React.useEffect(() => {
    dispatch(getDivisionsAsync.request());
    if (!isCreate && code) {
      const codeNumber = Number(code);
      if (!Number.isNaN(codeNumber)) {
        dispatch(getInvoiceItemCategoryDetailAsync.request(codeNumber));
      }
    }
  }, [code, isCreate, dispatch]);

  // Set default division to Blogic when divisions are loaded (create mode)
  React.useEffect(() => {
    if (isCreate && divisions.length > 0 && !divisionID) {
      const blogic = divisions.find(d => d.name === BLOGIC_DIVISION_NAME);
      setDivisionID(blogic?.divisionID ?? divisions[0].divisionID);
    }
  }, [divisions, isCreate, divisionID]);

  React.useEffect(() => {
    if (detail && !isCreate) {
      setName(detail.name);
      setIsCode(detail.isCode ?? "");
      setDivisionID(detail.divisionID);
    }
  }, [detail, isCreate]);

  // Redirect after successful save (ignore initial mount value)
  const hasMounted = React.useRef(false);
  React.useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      return;
    }
    if (saveSuccess) {
      history.push(`/${t(Resources.Routes.InvoiceItemCategories)}`);
    }
  }, [saveSuccess, history, t]);

  const MAX_LENGTH = 128;

  const validate = (): boolean => {
    const newErrors: ValidationErrors = {};
    const requiredMsg = t(
      Resources.InvoiceItemCategories.Detail.Validation.Required,
    );
    const maxLengthMsg = t(
      Resources.InvoiceItemCategories.Detail.Validation.MaxLength,
      { max: MAX_LENGTH },
    );

    if (!name.trim()) {
      newErrors.name = requiredMsg;
    } else if (name.length > MAX_LENGTH) {
      newErrors.name = maxLengthMsg;
    }
    if (isCreate && !isCode.trim()) {
      newErrors.isCode = requiredMsg;
    } else if (isCreate && isCode.length > MAX_LENGTH) {
      newErrors.isCode = maxLengthMsg;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const save = () => {
    if (!validate()) {
      return;
    }

    dispatch(
      saveInvoiceItemCategoryAsync.request({
        invoiceItemCategoryCode: isCreate
          ? undefined
          : detail!.invoiceItemCategoryCode,
        name,
        isCode: isCreate ? isCode : detail!.isCode,
        divisionID,
        isNew: isCreate,
      }),
    );
  };

  const getBreadcrumbItems = (): BreadcrumbItem[] => {
    return [
      {
        name: t(Resources.InvoiceItemCategories.Title),
        url: t(Resources.Routes.InvoiceItemCategories),
      },
      {
        name: isCreate
          ? t(Resources.InvoiceItemCategories.Detail.BreadcrumbCreate)
          : t(Resources.InvoiceItemCategories.Detail.BreadcrumbEdit),
      },
    ];
  };

  const showForm = isCreate || (!isLoading && detail);

  return (
    <AuthenticatedLayout
      title={
        isCreate
          ? t(Resources.InvoiceItemCategories.Detail.TitleCreate)
          : detail?.name ?? ""
      }
      breadcrumbItems={getBreadcrumbItems()}
    >
      <div className={"container-small"}>
        <div className={"box detail"}>
          <div className={"box-header"}>
            {showForm ? (
              isCreate ? (
                t(Resources.InvoiceItemCategories.Detail.TitleCreate)
              ) : (
                t(Resources.InvoiceItemCategories.Detail.TitleEdit)
              )
            ) : (
              <PrimarySkeleton height={30} />
            )}
          </div>
          <div className={"box-content"}>
            {showForm ? (
              <>
                <StyledBasicInfo>
                  <StyledFormGrid>
                    <div>
                      <TextInput
                        label={t(Resources.InvoiceItemCategories.Detail.Name)}
                        name="name"
                        value={name}
                        onChange={e => {
                          setName(e.target.value);
                          if (errors.name)
                            setErrors(prev => ({ ...prev, name: undefined }));
                        }}
                        error={!!errors.name}
                        helperText={errors.name}
                        inputProps={{ maxLength: MAX_LENGTH }}
                      />
                    </div>

                    <div>
                      <TextInput
                        label={t(Resources.InvoiceItemCategories.Detail.ISCode)}
                        name="isCode"
                        value={isCreate ? isCode : detail?.isCode ?? ""}
                        onChange={e => {
                          setIsCode(e.target.value);
                          if (errors.isCode)
                            setErrors(prev => ({ ...prev, isCode: undefined }));
                        }}
                        disabled={!isCreate}
                        error={!!errors.isCode}
                        helperText={errors.isCode}
                        inputProps={{ maxLength: MAX_LENGTH }}
                      />
                    </div>

                    {divisions.length > 0 && (
                      <div className={"selectbox"}>
                        <StyledTitle className={"form-label"}>
                          {t(Resources.InvoiceItemCategories.Detail.Division)}
                        </StyledTitle>
                        <Select
                          id={"division-select"}
                          value={divisionID}
                          onChange={e =>
                            setDivisionID(e.target.value as number)
                          }
                        >
                          {divisions.map(d => (
                            <MenuItem key={d.divisionID} value={d.divisionID}>
                              {d.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </div>
                    )}
                  </StyledFormGrid>
                </StyledBasicInfo>

                <PrimaryButton onClick={save} disabled={isUpdating}>
                  {t(Resources.InvoiceItemCategories.Detail.Save)}
                </PrimaryButton>
              </>
            ) : (
              <>
                <PrimarySkeleton height={60} />
                <PrimarySkeleton height={60} />
                <PrimarySkeleton height={60} />
                <PrimarySkeleton height={60} />
              </>
            )}
          </div>
        </div>
      </div>
    </AuthenticatedLayout>
  );
};
